/**
 * On-device log of what each scan actually changed.
 *
 * This replaces `scanHistoryStore`, which modelled a scan as a list of
 * INDIVIDUAL Pokémon (CP / HP / level / IV / Lucky / 100%) in the shape of the
 * Poké Genie scan organizer. That model never matched this scanner: its only
 * writer was LiveScanModal, deleted in PR-D3 (0defd6a), and it has recorded
 * nothing since. The scanner does not read individual Pokémon — it reads the
 * Pokédex, and what it produces is dex writes: species × variant → present.
 *
 * So the unit here is the SCAN, not the Pokémon. One session is one run of the
 * live scanner (or one screenshot upload), and its rows are the cells that run
 * added to, or removed from, the trainer's collection. A scan that swept 300
 * cells and changed four shows four rows — the log answers "what did that scan
 * do to my dex?", which is the only question worth asking of it, and the one
 * you need answered before you can undo a bad write.
 *
 * Recording is passive. `beginScanSession` installs a recorder on dexStateStore
 * and `endScanSession` removes it, so EVERY scan write path — live grid, live
 * detail page, screenshot upload, the self-correction ratchet — lands here
 * without each call site having to remember to log. Nothing outside an open
 * session is ever attributed to one.
 *
 * Storage:
 *   - localStorage, single JSON blob, newest session first.
 *   - Capped at MAX_SESSIONS runs and MAX_ROWS_PER_SESSION rows each.
 *   - Device-local, like the dex state it describes. It does not survive a
 *     reinstall, and the page says so rather than implying a server backup.
 *
 * Every write is try/catched: a failed log must never break a scan.
 */
import {
  setScanWriteRecorder,
  undoScanWrite,
  type ScanWriteRecord,
} from "./dexStateStore";
import { deleteMyPokemon } from "./myPokemonStore";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** Undo one My Pokémon row: delete the record the scan created. */
function undoMyPokemonRow(recordId: string): boolean {
  try {
    return deleteMyPokemon(recordId);
  } catch {
    return false;
  }
}

const SCAN_SESSIONS_KEY = "nexus.scanSessions.v1";
const MAX_SESSIONS = 40;
const MAX_ROWS_PER_SESSION = 600;
export const SCAN_SESSION_EVENT = "nexus:scan-sessions-changed";

/** How the scan was run. */
export type ScanSessionKind = "live" | "upload";

/** What the scan did to a cell, from the trainer's point of view. */
export type ScanRowAction = "added" | "removed";

/**
 * What a row describes: a Pokédex cell write ("dex", the original model) or an
 * individual caught Pokémon saved to My Pokémon ("myPokemon", 2026-08-28).
 * Both kinds of scan land in the same log so the page answers "what did that
 * scan do?" for either, but the page renders them distinctly: a dex row gets
 * its variant badge, a Pokémon row gets a My Pokémon chip.
 */
export type ScanRowTarget = "dex" | "myPokemon";

export interface ScanSessionRow {
  /**
   * Identity of the CELL, not of the event: `variant:dexNumber:formId`. A scan
   * that adds a cell and then self-corrects it away updates this row in place
   * rather than appending a second one, so the log shows the net effect of the
   * run instead of narrating the scanner arguing with itself.
   * My Pokémon rows use `myPokemon:<recordId>` — record identity, same idea.
   */
  key: string;
  speciesId: number;
  variant: PokedexVariant;
  formId: string | null;
  action: ScanRowAction;
  ts: number;
  /** This write also lit the species' MAIN row through the cascade. */
  cascadedMain: boolean;
  /** The trainer has undone this row. Kept, not deleted — see undoScanRow. */
  undone: boolean;
  /** What this row describes. Absent in pre-2026-08-28 data = "dex". */
  target: ScanRowTarget;
  /** The My Pokémon record this row created. Only on target "myPokemon". */
  myPokemonId: string | null;
}

export interface ScanSession {
  id: string;
  kind: ScanSessionKind;
  startedAt: number;
  /** null while the scan is still running. */
  endedAt: number | null;
  rows: ScanSessionRow[];
}

interface ScanSessionShape {
  sessions: ScanSession[];
}

function isRowAction(v: unknown): v is ScanRowAction {
  return v === "added" || v === "removed";
}

function normalizeRow(raw: unknown): ScanSessionRow | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<ScanSessionRow> & Record<string, unknown>;
  if (typeof r.speciesId !== "number" || !Number.isFinite(r.speciesId)) return null;
  if (typeof r.variant !== "string" || r.variant.length === 0) return null;
  if (!isRowAction(r.action)) return null;
  const formId = typeof r.formId === "string" && r.formId.length > 0 ? r.formId : null;
  return {
    key:
      typeof r.key === "string" && r.key.length > 0
        ? r.key
        : rowKey(r.variant as PokedexVariant, r.speciesId, formId),
    speciesId: r.speciesId,
    variant: r.variant as PokedexVariant,
    formId,
    action: r.action,
    ts: typeof r.ts === "number" ? r.ts : 0,
    cascadedMain: !!r.cascadedMain,
    undone: !!r.undone,
    target: r.target === "myPokemon" ? "myPokemon" : "dex",
    myPokemonId:
      typeof r.myPokemonId === "string" && r.myPokemonId.length > 0 ? r.myPokemonId : null,
  };
}

function normalizeSession(raw: unknown): ScanSession | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<ScanSession> & Record<string, unknown>;
  if (typeof s.id !== "string" || s.id.length === 0) return null;
  if (typeof s.startedAt !== "number" || !Number.isFinite(s.startedAt)) return null;
  const rows: ScanSessionRow[] = [];
  if (Array.isArray(s.rows)) {
    for (const item of s.rows) {
      const row = normalizeRow(item);
      if (row) rows.push(row);
    }
  }
  return {
    id: s.id,
    kind: s.kind === "upload" ? "upload" : "live",
    startedAt: s.startedAt,
    endedAt: typeof s.endedAt === "number" ? s.endedAt : null,
    rows,
  };
}

function readRaw(): ScanSessionShape {
  try {
    if (typeof window === "undefined") return { sessions: [] };
    const raw = window.localStorage.getItem(SCAN_SESSIONS_KEY);
    if (!raw) return { sessions: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.sessions)) return { sessions: [] };
    const sessions: ScanSession[] = [];
    for (const item of parsed.sessions) {
      const session = normalizeSession(item);
      if (session) sessions.push(session);
    }
    return { sessions };
  } catch {
    return { sessions: [] };
  }
}

function writeRaw(shape: ScanSessionShape): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SCAN_SESSIONS_KEY, JSON.stringify(shape));
  } catch {
    /* quota exceeded — drop the log rather than the scan. */
  }
}

function emit(): void {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(SCAN_SESSION_EVENT));
  } catch {
    /* SSR or non-DOM runtime — silent. */
  }
}

export function rowKey(
  variant: PokedexVariant,
  speciesId: number,
  formId: string | null,
): string {
  return formId ? `${variant}:${speciesId}:${formId}` : `${variant}:${speciesId}`;
}

/**
 * THE OPEN SESSION LIVES IN MEMORY, and that is a performance decision, not a
 * convenience one.
 *
 * The obvious implementation — read the blob, add the row, write the blob — puts
 * a JSON parse and stringify of the ENTIRE log, plus a synchronous localStorage
 * write, on the path of every cell a scan changes. That is a per-write cost that
 * grows with the log, on the one code path in the app that must not get slower:
 * a frame the scanner spends waiting on localStorage is a frame it did not OCR,
 * and a starved scan drops rows (see the pass-5 1fps starvation).
 *
 * So a running scan touches nothing but this object. Storage is written on a
 * row-count checkpoint — cheap insurance against losing the log if the app dies
 * mid-scan, since the dex writes themselves have already landed — and again when
 * the session closes.
 *
 * The change event is NOT fired on those checkpoints. It is a page-level "the
 * log moved" signal, and the Pokédex page turns it into a completion refetch;
 * firing it per changed cell meant a network request per catch. Nobody is
 * looking at the history page mid-scan — the scanner owns the screen — so one
 * event at the end is both cheaper and more honest.
 */
interface OpenSession {
  session: ScanSession;
  /** key → row, so a re-observation is a lookup rather than a linear scan. */
  index: Map<string, ScanSessionRow>;
  /** Rows added since the last checkpoint. */
  dirty: number;
}

let open: OpenSession | null = null;

/** Rows between storage checkpoints while a scan is running. */
const FLUSH_EVERY_ROWS = 25;

function persistOpenSession(): void {
  if (!open) return;
  try {
    const shape = readRaw();
    const index = shape.sessions.findIndex((s) => s.id === open!.session.id);
    // Structured-clone the in-memory rows so a later mutation cannot reach back
    // through the persisted copy.
    const snapshot: ScanSession = {
      ...open.session,
      rows: open.session.rows.map((r) => ({ ...r })),
    };
    if (index >= 0) shape.sessions[index] = snapshot;
    else shape.sessions.unshift(snapshot);
    shape.sessions.sort((a, b) => b.startedAt - a.startedAt);
    writeRaw({ sessions: shape.sessions.slice(0, MAX_SESSIONS) });
    open.dirty = 0;
  } catch {
    /* never let the log break the scan. */
  }
}

function mutatePersisted(fn: (shape: ScanSessionShape) => boolean): void {
  try {
    const shape = readRaw();
    if (!fn(shape)) return;
    shape.sessions.sort((a, b) => b.startedAt - a.startedAt);
    writeRaw({ sessions: shape.sessions.slice(0, MAX_SESSIONS) });
    emit();
  } catch {
    /* never let the log break the caller. */
  }
}

function ingest(sessionId: string, record: ScanWriteRecord): void {
  if (!open || open.session.id !== sessionId) return;
  const key = rowKey(record.variant, record.dexNumber, record.formId);
  const now = Date.now();
  const existing = open.index.get(key);
  if (existing) {
    // Same cell, later verdict: the run's net effect on this cell is whatever it
    // decided LAST. An add the ratchet then retracted reads as "removed", not as
    // two contradictory rows.
    if (existing.action === record.action && !existing.undone) return;
    existing.action = record.action;
    existing.ts = now;
    existing.cascadedMain = record.cascadedMain;
    existing.undone = false;
  } else {
    if (open.session.rows.length >= MAX_ROWS_PER_SESSION) return;
    const row: ScanSessionRow = {
      key,
      speciesId: record.dexNumber,
      variant: record.variant,
      formId: record.formId,
      action: record.action,
      ts: now,
      cascadedMain: record.cascadedMain,
      undone: false,
      target: "dex",
      myPokemonId: null,
    };
    open.session.rows.push(row);
    open.index.set(key, row);
  }
  if (++open.dirty >= FLUSH_EVERY_ROWS) persistOpenSession();
}

/**
 * Record an individual Pokémon a scan CREATED in My Pokémon, into whatever
 * session is open (live scan or screenshot upload). Passive like the dex
 * recorder: no open session, no row. Only creations are logged — a re-scan
 * that merged details into an existing record is an update to a catch the log
 * already knows about, and undoing it by deleting the record would destroy
 * pre-scan data. Same in-memory + checkpoint cost model as `ingest`, so the
 * live scan's hot path stays untouched.
 */
export function recordMyPokemonScanRow(entry: {
  myPokemonId: string;
  speciesId: number;
  formId: string | null;
}): void {
  if (!open) return;
  const key = `myPokemon:${entry.myPokemonId}`;
  if (open.index.has(key)) return;
  if (open.session.rows.length >= MAX_ROWS_PER_SESSION) return;
  const row: ScanSessionRow = {
    key,
    speciesId: entry.speciesId,
    // The variant axis does not apply to a My Pokémon record; "pokemon" keeps
    // the field total for old readers, and the page keys rendering off
    // `target`, never off this value.
    variant: "pokemon",
    formId: entry.formId,
    action: "added",
    ts: Date.now(),
    cascadedMain: false,
    undone: false,
    target: "myPokemon",
    myPokemonId: entry.myPokemonId,
  };
  open.session.rows.push(row);
  open.index.set(key, row);
  if (++open.dirty >= FLUSH_EVERY_ROWS) persistOpenSession();
}

/**
 * Open a session and start recording dex writes into it.
 *
 * Self-healing: opening a session closes any session left open by a scan that
 * crashed or was torn down on a path that never reached `endScanSession`, so a
 * stale recorder can never attribute a later scan's writes to an old run.
 */
export function beginScanSession(id: string, kind: ScanSessionKind = "live"): void {
  if (typeof id !== "string" || id.length === 0) return;
  if (open && open.session.id !== id) endScanSession(open.session.id);
  if (!open || open.session.id !== id) {
    open = {
      session: { id, kind, startedAt: Date.now(), endedAt: null, rows: [] },
      index: new Map(),
      dirty: 0,
    };
  }
  setScanWriteRecorder((record) => ingest(id, record));
}

/**
 * Close a session. A run that changed nothing is dropped entirely rather than
 * left as an empty card — "you scanned and nothing happened" is what the page's
 * own copy is for, and forty empty cards would bury the runs that did something.
 */
export function endScanSession(id: string): void {
  if (!open || open.session.id !== id) return;
  const finished = open.session;
  setScanWriteRecorder(null);
  if (finished.rows.length === 0) {
    // Nothing to keep. It may still have reached storage on a checkpoint if the
    // rows were all later collapsed away, so clear it there too.
    open = null;
    mutatePersisted((shape) => {
      const index = shape.sessions.findIndex((s) => s.id === id);
      if (index < 0) return false;
      shape.sessions.splice(index, 1);
      return true;
    });
    return;
  }
  finished.endedAt = Date.now();
  persistOpenSession();
  open = null;
  emit();
}

/**
 * Every session, newest first — including one still running, so opening the page
 * mid-scan shows the run in progress rather than pretending it isn't happening.
 */
export function listScanSessions(): ScanSession[] {
  const sessions = readRaw().sessions.filter((s) => s.id !== open?.session.id);
  if (open) sessions.push({ ...open.session, rows: open.session.rows.map((r) => ({ ...r })) });
  return sessions.sort((a, b) => b.startedAt - a.startedAt);
}

/**
 * Undo one row on the trainer's instruction.
 *
 * Only an `added` row can be undone — there is nothing to put back for a
 * removal, since the scanner retracting its own false catch leaves the cell in
 * the state the trainer would have chosen anyway.
 *
 * The row is MARKED undone rather than deleted. The scan really did add that
 * species, and the log is a record of what happened; erasing the row would make
 * the same misread look like it never occurred, and leave the trainer wondering
 * whether the undo landed. Returns false when the dex write could not be undone
 * (the row was since edited by hand, or is no longer scan-owned), and the row is
 * left alone so the button does not lie about what it did.
 */
export function undoScanRow(sessionId: string, key: string): boolean {
  const live = open?.session.id === sessionId ? open.index.get(key) : undefined;
  const row = live ?? listScanSessions().find((s) => s.id === sessionId)?.rows.find((r) => r.key === key);
  if (!row) return false;
  if (row.action !== "added" || row.undone) return false;
  if (row.target === "myPokemon") {
    // Undoing a Pokémon row deletes the record the scan created. Only
    // creations are ever logged (see recordMyPokemonScanRow), so this can
    // never destroy a record that predated the scan. A record already gone
    // (trainer deleted it by hand) fails honestly, like an edited dex cell.
    if (!row.myPokemonId || !undoMyPokemonRow(row.myPokemonId)) return false;
  } else if (!undoScanWrite(row.variant, row.speciesId, row.formId)) return false;
  if (live) {
    live.undone = true;
    persistOpenSession();
    emit();
    return true;
  }
  mutatePersisted((next) => {
    const target = next.sessions
      .find((s) => s.id === sessionId)
      ?.rows.find((r) => r.key === key);
    if (!target || target.undone) return false;
    target.undone = true;
    return true;
  });
  return true;
}

export function clearScanSessions(): number {
  const before = readRaw().sessions.length;
  open = null;
  setScanWriteRecorder(null);
  writeRaw({ sessions: [] });
  emit();
  return before;
}

/** Test seam: forget the in-memory open session. */
export function __resetScanSessionsForTest(): void {
  open = null;
  setScanWriteRecorder(null);
}
